'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { CheckCircle, Calendar, Users, Phone, ArrowRight, Loader2 } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';

interface DemoForm {
  name: string;
  email: string;
  phone: string;
  company: string;
  role: string;
  size: string;
  message: string;
}

const benefits = [
  'Tour personalizzato della piattaforma',
  'Risposta a tutte le tue domande',
  'Configurazione su misura per il tuo laboratorio',
  'Prova gratuita di 14 giorni inclusa',
];

export default function DemoPage() {
  const [form, setForm] = useState<DemoForm>({ name: '', email: '', phone: '', company: '', role: '', size: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const update = (k: keyof DemoForm, v: string) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.company) { setError('Nome, email e azienda sono obbligatori.'); return; }
    setLoading(true);
    setError(null);
    try {
      // Store lead via notifications/email API
      await fetch('/api/notifications/email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: 'sales@labcallmanager.com',
          subject: `Nuova richiesta demo da ${form.company}`,
          body: JSON.stringify(form, null, 2),
        }),
      });
      setSubmitted(true);
    } catch {
      setError('Errore nell\'invio. Riprova o contattaci direttamente.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle size={32} className="text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-foreground mb-3">Richiesta ricevuta!</h2>
          <p className="text-muted-foreground mb-6">Ti contatteremo entro 24 ore per fissare la demo. Nel frattempo puoi iniziare la prova gratuita.</p>
          <Link href="/register/lab" className="px-6 py-3 bg-primary text-white rounded-xl font-semibold hover:bg-primary/90 transition-colors inline-flex items-center gap-2">
            Inizia la prova gratuita <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur border-b border-border">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link href="/" className="flex items-center gap-2">
            <AppLogo size={28} />
            <span className="font-semibold text-sm text-foreground">LabCall<span className="text-primary">Manager</span></span>
          </Link>
          <Link href="/register/lab" className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90">Inizia Gratis</Link>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-4 py-16">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Left */}
          <div>
            <h1 className="text-4xl font-bold text-foreground mb-4">Richiedi una Demo Gratuita</h1>
            <p className="text-lg text-muted-foreground mb-8">Scopri come LabCallManager può trasformare la gestione del tuo laboratorio in 30 minuti.</p>
            <ul className="space-y-3 mb-10">
              {benefits.map((b) => (
                <li key={b} className="flex items-center gap-3 text-sm text-foreground">
                  <CheckCircle size={18} className="text-green-500 shrink-0" />
                  {b}
                </li>
              ))}
            </ul>
            <div className="grid grid-cols-3 gap-4">
              {[
                { icon: Calendar, label: '30 min', sub: 'Durata demo' },
                { icon: Users, label: '500+', sub: 'Clienti attivi' },
                { icon: Phone, label: '2M+', sub: 'Chiamate gestite' },
              ].map((s) => (
                <div key={s.label} className="p-4 rounded-xl bg-muted/50 text-center">
                  <s.icon size={20} className="text-primary mx-auto mb-2" />
                  <p className="font-bold text-foreground">{s.label}</p>
                  <p className="text-xs text-muted-foreground">{s.sub}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Form */}
          <div className="bg-white rounded-2xl border border-border p-8 shadow-sm">
            <h2 className="text-xl font-bold text-foreground mb-6">Compila il form</h2>
            {error && <div className="mb-4 p-3 bg-destructive/10 text-destructive rounded-lg text-sm">{error}</div>}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-foreground mb-1.5">Nome e Cognome *</label>
                  <input value={form.name} onChange={e => update('name', e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-border text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" placeholder="Mario Rossi" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground mb-1.5">Email *</label>
                  <input type="email" value={form.email} onChange={e => update('email', e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-border text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" placeholder="mario@lab.it" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-foreground mb-1.5">Telefono</label>
                  <input value={form.phone} onChange={e => update('phone', e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-border text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" placeholder="+39 02 1234567" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground mb-1.5">Azienda *</label>
                  <input value={form.company} onChange={e => update('company', e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-border text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" placeholder="Laboratorio Rossi" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-foreground mb-1.5">Ruolo</label>
                  <select value={form.role} onChange={e => update('role', e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-border text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 bg-white">
                    <option value="">Seleziona...</option>
                    <option>Direttore / CEO</option>
                    <option>Responsabile Operativo</option>
                    <option>IT Manager</option>
                    <option>Altro</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground mb-1.5">Dimensione laboratorio</label>
                  <select value={form.size} onChange={e => update('size', e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-border text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 bg-white">
                    <option value="">Seleziona...</option>
                    <option>1-5 operatori</option>
                    <option>6-15 operatori</option>
                    <option>16-50 operatori</option>
                    <option>50+ operatori</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground mb-1.5">Messaggio (opzionale)</label>
                <textarea value={form.message} onChange={e => update('message', e.target.value)} rows={3} className="w-full px-3 py-2.5 rounded-lg border border-border text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none" placeholder="Descrivi brevemente le tue esigenze..." />
              </div>
              <button type="submit" disabled={loading} className="w-full py-3 bg-primary text-white rounded-xl font-semibold text-sm hover:bg-primary/90 transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                {loading ? <Loader2 size={16} className="animate-spin" /> : <><Calendar size={16} /> Prenota la Demo</>}
              </button>
              <p className="text-xs text-muted-foreground text-center">Nessun impegno. Risponderemo entro 24 ore.</p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
